import * as L from 'leaflet';
import { FrameHistory } from '../models/manager-content-vehicle-history-drawer.model';

/**
 * Interfaz para definir las opciones de las flechas direccionales
 */
export interface DirectionArrowOptions {
  color?: string;
  weight?: number;
  opacity?: number;
  arrowSize?: number;
  arrowSpacing?: number; // Distancia entre flechas en metros
}

/**
 * Opciones por defecto para las flechas direccionalesx
 */
const DEFAULT_ARROW_OPTIONS: DirectionArrowOptions = {
  color: '#1976D2',
  weight: 2,
  opacity: 0.8,
  arrowSize: 8,
  arrowSpacing: 100, // 100 metros entre flechas
};

/**
 * Calcula la distancia entre dos puntos geográficos usando la fórmula de Haversine
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Radio de la Tierra en metros
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Interpola un punto entre dos coordenadas
 */
function interpolatePoint(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
  ratio: number
): [number, number] {
  const lat = lat1 + (lat2 - lat1) * ratio;
  const lng = lng1 + (lng2 - lng1) * ratio;
  return [lat, lng];
}

/**
 * Calcula el bearing (dirección) entre dos puntos
 */
function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  
  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  
  const bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
}

/**
 * Crea un marcador de flecha direccional
 */
function createArrowMarker(
  lat: number,
  lng: number,
  angle: number,
  options: DirectionArrowOptions
): L.Marker {
  // Crear el icono SVG de la flecha
  const arrowSvg = `
    <svg width="${options.arrowSize}" height="${options.arrowSize}" viewBox="0 0 24 24" 
         style="transform: rotate(${angle}deg);">
      <path d="M12 2L22 12L12 22L12 17L2 17L2 7L12 7Z" 
            fill="${options.color}" 
            stroke="white" 
            stroke-width="1" 
            opacity="${options.opacity}"/>
    </svg>
  `;

  const arrowIcon = L.divIcon({
    html: arrowSvg,
    className: 'direction-arrow-icon',
    iconSize: [options.arrowSize!, options.arrowSize!],
    iconAnchor: [options.arrowSize! / 2, options.arrowSize! / 2],
  });

  return L.marker([lat, lng], { icon: arrowIcon, interactive: false });
}

/**
 * Crea flechas direccionales a lo largo de una ruta basada en frames de historial
 */
export function createDirectionArrows(
  frames: FrameHistory[],
  options: Partial<DirectionArrowOptions> = {}
): L.Marker[] {
  const arrowOptions = { ...DEFAULT_ARROW_OPTIONS, ...options };
  const arrows: L.Marker[] = [];

  if (frames.length < 2) {
    return arrows;
  }

  let accumulatedDistance = 0;
  let lastArrowDistance = 0;

  for (let i = 0; i < frames.length - 1; i++) {
    const currentFrame = frames[i];
    const nextFrame = frames[i + 1];

    const lat1 = parseFloat(currentFrame.lat);
    const lng1 = parseFloat(currentFrame.lng);
    const lat2 = parseFloat(nextFrame.lat);
    const lng2 = parseFloat(nextFrame.lng);

    const segmentDistance = calculateDistance(lat1, lng1, lat2, lng2);
    accumulatedDistance += segmentDistance;

    // Verificar si es momento de colocar una flecha
    if (accumulatedDistance - lastArrowDistance >= arrowOptions.arrowSpacing!) {
      // Usar el ángulo del frame actual si está disponible, sino calcular el bearing
      let angle = currentFrame.angle || calculateBearing(lat1, lng1, lat2, lng2);
      
      // Ajustar el ángulo para que la flecha apunte en la dirección correcta
      angle = (angle + 90) % 360; // Rotar 90 grados para que la punta apunte hacia adelante

      // Crear la flecha en la posición del frame actual
      const arrow = createArrowMarker(lat1, lng1, angle, arrowOptions);
      arrows.push(arrow);

      lastArrowDistance = accumulatedDistance;
    }
  }

  // Agregar una flecha al final de la ruta
  const lastFrame = frames[frames.length - 1];
  const secondLastFrame = frames[frames.length - 2];
  
  if (lastFrame && secondLastFrame) {
    const lat1 = parseFloat(secondLastFrame.lat);
    const lng1 = parseFloat(secondLastFrame.lng);
    const lat2 = parseFloat(lastFrame.lat);
    const lng2 = parseFloat(lastFrame.lng);
    
    let angle = lastFrame.angle || calculateBearing(lat1, lng1, lat2, lng2);
    angle = (angle + 90) % 360;
    
    const finalArrow = createArrowMarker(
      parseFloat(lastFrame.lat),
      parseFloat(lastFrame.lng),
      angle,
      arrowOptions
    );
    arrows.push(finalArrow);
  }

  return arrows;
}

/**
 * Crea flechas direccionales para segmentos de velocidad
 */
export function createDirectionArrowsForSpeedSegments(
  frames: FrameHistory[],
  options: Partial<DirectionArrowOptions> = {}
): L.Marker[] {
  const arrowOptions = { 
    ...DEFAULT_ARROW_OPTIONS, 
    ...options,
    arrowSpacing: 200 // Mayor espaciado para segmentos de velocidad
  };
  
  return createDirectionArrows(frames, arrowOptions);
}

/**
 * Obtiene el color de la flecha basado en la velocidad
 */
export function getArrowColorBySpeed(speed: number): string {
  if (speed <= 0) return '#9E9E9E'; // Gris para parado
  if (speed <= 30) return '#4CAF50'; // Verde para velocidad baja
  if (speed <= 60) return '#FF9800'; // Naranja para velocidad media
  if (speed <= 90) return '#F44336'; // Rojo para velocidad alta
  return '#9C27B0'; // Púrpura para velocidad muy alta
}

/**
 * Crea flechas direccionales con colores basados en la velocidad
 */
export function createSpeedBasedDirectionArrows(
  frames: FrameHistory[],
  baseOptions: Partial<DirectionArrowOptions> = {}
): L.Marker[] {
  const arrows: L.Marker[] = [];
  
  if (frames.length < 2) {
    return arrows;
  }

  let accumulatedDistance = 0;
  let lastArrowDistance = 0;
  const arrowSpacing = baseOptions.arrowSpacing || DEFAULT_ARROW_OPTIONS.arrowSpacing!;

  for (let i = 0; i < frames.length - 1; i++) {
    const currentFrame = frames[i];
    const nextFrame = frames[i + 1];

    const lat1 = parseFloat(currentFrame.lat);
    const lng1 = parseFloat(currentFrame.lng);
    const lat2 = parseFloat(nextFrame.lat);
    const lng2 = parseFloat(nextFrame.lng);

    const segmentDistance = calculateDistance(lat1, lng1, lat2, lng2);
    accumulatedDistance += segmentDistance;

    if (accumulatedDistance - lastArrowDistance >= arrowSpacing) {
      const speed = currentFrame.speed || 0;
      const color = getArrowColorBySpeed(speed);
      
      let angle = currentFrame.angle || calculateBearing(lat1, lng1, lat2, lng2);
      angle = (angle + 90) % 360;

      const arrowOptions = {
        ...DEFAULT_ARROW_OPTIONS,
        ...baseOptions,
        color
      };

      const arrow = createArrowMarker(lat1, lng1, angle, arrowOptions);
      arrows.push(arrow);

      lastArrowDistance = accumulatedDistance;
    }
  }

  return arrows;
}